import React from 'react';
import { Sparkles } from 'lucide-react';
import { parseOmdPriceNumber, type OmdProduct } from '../../../utils/omdProductView';
import type { OmdPanelMaterial } from '../types';

type OmdVkCalculatorProps = {
  product: OmdProduct;
  material: OmdPanelMaterial;
};

function formatEuro(amount: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function OmdVkCalculator({ product, material }: OmdVkCalculatorProps) {
  const ek = parseOmdPriceNumber(product.prices?.netPrice);
  const initialSurcharge =
    typeof material.surcharge_percent === 'number' && Number.isFinite(material.surcharge_percent)
      ? material.surcharge_percent
      : 25;
  const [surcharge, setSurcharge] = React.useState(() => Math.round(initialSurcharge));

  if (ek == null) {
    return <p className="text-sm text-app-text-subtle">Kein Netto-EK für VK-Kalkulation verfügbar.</p>;
  }

  const vk = ek * (1 + surcharge / 100);
  const tickMarks = [25, 50, 75];

  return (
    <div className="rounded-lg border border-app-border bg-app-surface overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-app-border">
        <Sparkles className="w-4 h-4 shrink-0 text-pipedrive-blue" aria-hidden />
        <h3 className="font-semibold text-app-text">VK-Kalkulation</h3>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-4">
          <div className="shrink-0 min-w-[6.5rem]">
            <p className="text-xs font-medium text-app-text-subtle mb-1">EK (netto)</p>
            <div className="rounded-lg border border-app-border bg-app-surface px-3 py-2 min-h-[2.5rem] flex items-center">
              <p className="text-sm font-semibold tabular-nums text-app-text">{formatEuro(ek)}</p>
            </div>
          </div>

          <span className="pb-2.5 text-lg text-app-text-subtle font-light select-none" aria-hidden>
            ×
          </span>

          <div className="flex-1 min-w-[10rem] max-w-xs">
            <p className="text-xs font-medium text-app-text-subtle mb-1">Zuschlag {surcharge} %</p>
            <div className="relative w-full">
              <div
                className="pointer-events-none absolute inset-x-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-200"
                aria-hidden
              >
                {tickMarks.map((pct) => (
                  <span
                    key={pct}
                    className="absolute top-0 bottom-0 w-px bg-slate-400/70"
                    style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
                  />
                ))}
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={surcharge}
                onChange={(e) => setSurcharge(Number(e.target.value))}
                className="relative z-10 w-full h-2 rounded-full appearance-none cursor-pointer accent-pipedrive-blue bg-transparent"
                aria-label={`Zuschlag ${surcharge} Prozent`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={surcharge}
              />
            </div>
          </div>

          <span className="pb-2.5 text-lg text-app-text-subtle font-light select-none" aria-hidden>
            =
          </span>

          <div className="shrink-0 min-w-[6.5rem]">
            <p className="text-xs font-medium text-app-text-subtle mb-1">VK (netto)</p>
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 min-h-[2.5rem] flex items-center">
              <p className="text-sm font-semibold tabular-nums text-pipedrive-blue">{formatEuro(vk)}</p>
            </div>
          </div>
        </div>

        <p className="text-xs text-app-text-subtle leading-relaxed">
          EK × (1 + Zuschlag / 100) — nur zur Orientierung, Werte werden nicht gespeichert.
        </p>
      </div>
    </div>
  );
}
